import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { DataTable } from "@/modules/shell/data-table";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { listAccounts } from "@/shared/api/accounting";
import {
  createPayMethod,
  deactivatePayMethod,
  listPayMethods,
  updatePayMethod,
  type PayMethod,
} from "@/shared/api/pay-methods";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import { payMethodColumns } from "./pay-methods-columns";

const payMethodCreateSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  worksOffline: z.boolean(),
  glAccountCode: z.string().trim().min(1),
});
type PayMethodCreateInput = z.input<typeof payMethodCreateSchema>;

/** Advanced: add / rename / default / GL map / deactivate — plan/24 */
export function PayMethodsPage() {
  const { companySlug } = useParams();
  const queryClient = useQueryClient();
  const confirmUi = useConfirm();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const methodsQuery = useQuery({
    queryKey: queryKeys.payMethods.list(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () =>
      listPayMethods(companySlug!, { includeDeactivated: true }),
  });
  const accountsQuery = useQuery({
    queryKey: queryKeys.gl.accounts(companySlug ?? ""),
    enabled: Boolean(companySlug),
    queryFn: () => listAccounts(companySlug!),
  });
  const errorMsg = queryErrorMessage(methodsQuery.error, methodsQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  const assetAccounts = (accountsQuery.data?.accounts ?? []).filter(
    (a) => a.type === "asset" && a.deactivatedAt == null,
  );
  const defaultGl =
    assetAccounts.find((a) => a.code === "1000")?.code ??
    assetAccounts[0]?.code ??
    "1000";

  const createForm = useForm<PayMethodCreateInput>({
    resolver: zodResolver(payMethodCreateSchema),
    defaultValues: {
      name: "",
      worksOffline: false,
      glAccountCode: "1000",
    },
  });

  useEffect(() => {
    createForm.setValue("glAccountCode", defaultGl);
  }, [defaultGl, createForm]);

  async function reload() {
    if (!companySlug) return;
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.payMethods.all(companySlug),
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.gl.accounts(companySlug),
      }),
    ]);
  }

  const create = useMutation({
    mutationFn: async (raw: PayMethodCreateInput) => {
      const body = payMethodCreateSchema.parse(raw);
      return createPayMethod(companySlug!, {
        name: body.name,
        worksOffline: body.worksOffline,
        glAccountCode: body.glAccountCode,
      });
    },
    onSuccess: async () => {
      createForm.reset({
        name: "",
        worksOffline: false,
        glAccountCode: defaultGl,
      });
      setError(null);
      await reload();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Create failed");
    },
  });

  function accountLabel(code: string) {
    const a = assetAccounts.find((x) => x.code === code);
    return a ? `${a.code} · ${a.name}` : code;
  }

  function startEdit(m: PayMethod) {
    setEditingId(m.id);
    setEditName(m.name);
    setError(null);
  }

  function saveRename() {
    if (!companySlug || !editingId) return;
    const trimmed = editName.trim();
    const current = methodsQuery.data?.methods.find((x) => x.id === editingId);
    if (!trimmed || !current || trimmed === current.name) {
      setEditingId(null);
      setEditName("");
      return;
    }
    setBusy(true);
    setError(null);
    void updatePayMethod(companySlug, editingId, { name: trimmed })
      .then(async () => {
        setEditingId(null);
        setEditName("");
        await reload();
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Update failed"),
      )
      .finally(() => setBusy(false));
  }

  const columns = useMemo(
    () =>
      payMethodColumns({
        busy,
        editingId,
        editName,
        setEditName,
        onStartEdit: startEdit,
        onCancelEdit: () => {
          setEditingId(null);
          setEditName("");
        },
        onSaveRename: saveRename,
        assetAccounts,
        accountLabel,
        onSetGl: (m: PayMethod, code: string) => {
          if (!code || code === m.glAccountCode || !companySlug) return;
          setBusy(true);
          setError(null);
          void updatePayMethod(companySlug, m.id, { glAccountCode: code })
            .then(() => reload())
            .catch((err: unknown) =>
              setError(
                err instanceof Error ? err.message : "GL update failed",
              ),
            )
            .finally(() => setBusy(false));
        },
        onSetDefault: (m: PayMethod) => {
          if (!companySlug) return;
          setBusy(true);
          setError(null);
          void updatePayMethod(companySlug, m.id, {
            isDefault: true,
            reactivate: m.deactivatedAt != null,
          })
            .then(() => reload())
            .catch((err: unknown) =>
              setError(err instanceof Error ? err.message : "Failed"),
            )
            .finally(() => setBusy(false));
        },
        onToggleOffline: (m: PayMethod) => {
          if (!companySlug) return;
          setBusy(true);
          setError(null);
          void updatePayMethod(companySlug, m.id, {
            worksOffline: !m.worksOffline,
          })
            .then(() => reload())
            .catch((err: unknown) =>
              setError(err instanceof Error ? err.message : "Failed"),
            )
            .finally(() => setBusy(false));
        },
        onDeactivate: (m: PayMethod) => {
          void (async () => {
            if (!companySlug) return;
            const ok = await confirmUi({
              title: `Deactivate “${m.name}”?`,
              description:
                "It will hide from new tenders. Posted payments keep their method; you can reactivate later.",
              confirmLabel: "Deactivate",
              destructive: true,
            });
            if (!ok) return;
            setBusy(true);
            setError(null);
            try {
              await deactivatePayMethod(companySlug, m.id);
              await reload();
            } catch (err: unknown) {
              setError(err instanceof Error ? err.message : "Failed");
            } finally {
              setBusy(false);
            }
          })();
        },
        onReactivate: (m: PayMethod) => {
          if (!companySlug) return;
          setBusy(true);
          setError(null);
          void updatePayMethod(companySlug, m.id, { reactivate: true })
            .then(() => reload())
            .catch((err: unknown) =>
              setError(err instanceof Error ? err.message : "Failed"),
            )
            .finally(() => setBusy(false));
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- match CustomersPage pattern
    [
      busy,
      companySlug,
      assetAccounts,
      confirmUi,
      editingId,
      editName,
      methodsQuery.data,
    ],
  );

  if (!companySlug) return null;

  const methods = methodsQuery.data?.methods ?? [];
  const displayError =
    error ??
    (errorMsg && errorMsg !== "Not signed in" ? errorMsg : null);

  return (
    <PageFrame>
      <PageHeader
        title="Pay methods"
        description="Cash posts to Cash on hand; card / e-wallet / bank post to Bank. Advanced: remap GL."
      />

      {methodsQuery.isPending && (
        <Alert variant="muted" className="mt-8">
          Loading pay methods…
        </Alert>
      )}
      {!methodsQuery.isPending && methods.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            title="No pay methods"
            description="Add cash or another tender for POS and payments."
          />
        </div>
      ) : null}
      {!methodsQuery.isPending && methods.length > 0 ? (
        <DataTable
          listId="pay-methods"
          companySlug={companySlug}
          columns={columns}
          data={methods}
          getRowId={(r) => r.id}
          manualSorting={false}
        />
      ) : null}

      <form
        className="mt-8 space-y-3 border-t border-[var(--color-border)] pt-6"
        onSubmit={createForm.handleSubmit((values) => create.mutate(values))}
      >
        <h2 className="m-0 text-sm font-semibold">Add method</h2>
        <div>
          <Label htmlFor="pay-method-name">Name</Label>
          <Input
            id="pay-method-name"
            className="mt-1"
            placeholder="e.g. Maya"
            {...createForm.register("name")}
          />
          {createForm.formState.errors.name && (
            <p className="mt-1 text-xs text-[var(--color-destructive)]">
              {createForm.formState.errors.name.message}
            </p>
          )}
        </div>
        {assetAccounts.length > 0 && (
          <div>
            <Label htmlFor="pay-method-gl">GL account</Label>
            <Select
              id="pay-method-gl"
              className="mt-1"
              {...createForm.register("glAccountCode")}
            >
              {assetAccounts.map((a) => (
                <option key={a.id} value={a.code}>
                  {a.code} · {a.name}
                </option>
              ))}
            </Select>
          </div>
        )}
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input type="checkbox" {...createForm.register("worksOffline")} />
          Works offline (POS)
        </label>
        <Button
          type="submit"
          pending={busy || create.isPending}
          pendingLabel="Saving…"
        >
          Add
        </Button>
      </form>

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}

      <p className="mt-8">
        <Button variant="ghost" asChild>
          <Link to={`/c/${companySlug}`} className="no-underline">
            ← Company
          </Link>
        </Button>
      </p>
    </PageFrame>
  );
}
